<template>
    <Teleport to="body">
        <div
            v-if="show"
            class="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]"
            @click.self="$emit('close')"
            aria-labelledby="edit-history-modal"
            role="dialog"
            aria-modal="true"
        >
            <div
                class="flex w-full max-w-[600px] mx-4 p-6 flex-col items-start rounded-lg bg-white dark:bg-slate-800 shadow-lg shadow-black/10 dark:shadow-slate-900/50"
                @click.stop
            >
                <!-- Header -->
                <div
                    class="flex items-center justify-between self-stretch pb-8"
                >
                    <h2 class="text-slate-800 dark:text-slate-100 text-xl font-medium leading-7">
                        Edit Entry
                    </h2>
                    <button
                        class="flex p-2 items-center justify-center rounded border-0 bg-transparent hover:bg-gray-100 dark:bg-slate-800 dark:hover:bg-slate-700 transition-colors"
                        @click="$emit('close')"
                        type="button"
                        aria-label="Close modal"
                    >
                        <Icon
                            icon="close"
                            :width="20"
                            :height="20"
                            class="text-gray-500 dark:text-gray-400"
                        />
                    </button>
                </div>

                <!-- Content -->
                <div class="flex flex-col items-start self-stretch">
                    <!-- Program Section -->
                    <div
                        class="flex flex-col justify-center items-start self-stretch pb-6"
                    >
                        <div class="flex pb-2 items-start gap-2.5 self-stretch">
                            <span
                                class="flex-1 text-slate-600 dark:text-slate-300 text-sm font-medium leading-5"
                            >
                                Program
                            </span>
                        </div>
                        <div class="flex items-center gap-3 self-stretch">
                            <div
                                class="flex w-28 h-12 p-1.5 px-3.5 justify-center items-center rounded-lg border border-gray-200 dark:border-slate-700"
                            >
                                <img
                                    v-if="program?.image"
                                    :src="getProgramImageUrl(program.image)"
                                    :alt="program.name"
                                    class="max-w-full max-h-full object-contain"
                                />
                            </div>
                            <span
                                class="flex-1 text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                            >
                                {{ program?.name }}
                            </span>
                        </div>
                    </div>

                    <!-- Input Fields -->
                    <div class="flex items-start gap-2 self-stretch">
                        <!-- Points Balance Input -->
                        <div class="flex flex-col items-start flex-1">
                            <div
                                class="flex pb-2 items-start gap-2.5 self-stretch"
                            >
                                <span
                                    class="text-slate-600 dark:text-slate-300 text-sm font-medium leading-5"
                                >
                                    Points balance
                                </span>
                            </div>
                            <div
                                class="flex py-2.5 px-4 items-center gap-2 self-stretch rounded border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-700"
                            >
                                <input
                                    type="text"
                                    class="flex-1 text-slate-800 dark:text-slate-100 text-sm font-normal leading-5 border-0 outline-0 bg-transparent placeholder:text-gray-400 dark:placeholder:text-slate-500 !p-0"
                                    :value="editBalance"
                                    @input="$emit('update:editBalance', $event)"
                                    placeholder="Enter points"
                                />
                            </div>
                        </div>

                        <!-- Date Input -->
                        <div class="flex flex-col items-start flex-1">
                            <div
                                class="flex pb-2 items-start gap-2.5 self-stretch"
                            >
                                <span
                                    class="text-slate-600 dark:text-slate-300 text-sm font-medium leading-5"
                                >
                                    Date
                                </span>
                            </div>
                            <div class="relative w-full">
                                <div
                                    class="absolute left-0 top-0 bottom-0 w-10 flex items-center justify-center border-r border-slate-200 dark:border-slate-600 pointer-events-none"
                                >
                                    <Icon
                                        icon="calendar"
                                        :width="20"
                                        :height="20"
                                        class="text-slate-800 dark:text-slate-300"
                                    />
                                </div>
                                <flat-pickr
                                    id="edit-balance-date"
                                    :config="dateConfig"
                                    class="w-full py-2.5 pl-12 pr-4 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 text-sm font-normal leading-5 placeholder:text-slate-500 dark:placeholder:text-slate-400 focus:outline-none focus:border-slate-400 cursor-pointer"
                                    :modelValue="editBalanceDate"
                                    @update:modelValue="$emit('update:editBalanceDate', $event)"
                                    placeholder="Select date"
                                ></flat-pickr>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Footer Buttons -->
                <div
                    class="flex justify-end items-start gap-2 self-stretch pt-8"
                >
                    <button
                        type="button"
                        class="flex py-2.5 px-4 items-start gap-2 rounded border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm dark:shadow-slate-900/50 text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                        @click="$emit('close')"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        class="flex py-2.5 px-4 justify-center items-center gap-2 rounded bg-slate-800 dark:bg-slate-700 shadow-sm text-white text-sm font-medium leading-5 hover:bg-slate-700 dark:hover:bg-slate-600 transition-colors"
                        @click="$emit('confirm')"
                    >
                        Update
                    </button>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script setup>
import Icon from '@/components/ui/icon/icon.vue';
import flatPickr from '@/components/ui/SafeFlatPickr.vue';

const props = defineProps({
    show: {
        type: Boolean,
        required: true,
    },
    program: {
        type: Object,
        default: null,
    },
    editBalance: {
        type: String,
        required: true,
    },
    editBalanceDate: {
        type: String,
        required: true,
    },
    dateConfig: {
        type: Object,
        required: true,
    },
    getProgramImageUrl: {
        type: Function,
        required: true,
    },
});

defineEmits(['close', 'confirm', 'update:editBalance', 'update:editBalanceDate']);
</script>
